import {
  requestAuthed,
  resolveTokenFromAuthHeaders,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

export type PasskeyCredential = {
  id: string
  nickname: string | null
  createdAt: string | null
}

const PASSKEY_LIST_PATH = '/passkeys'
const PASSKEY_REGISTER_OPTIONS_PATH = '/passkeys/register/options'
const PASSKEY_REGISTER_VERIFY_PATH = '/passkeys/register/verify'

const toPayloadMessage = (payload: Record<string, unknown>, fallback: string): string => {
  const message = payload.message
  if (typeof message === 'string' && message.trim()) return message.trim()
  return fallback
}

const toCredential = (value: unknown): PasskeyCredential | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (typeof raw.id !== 'string' || !raw.id.trim()) return null
  return {
    id: raw.id.trim(),
    nickname: typeof raw.nickname === 'string' && raw.nickname.trim() ? raw.nickname.trim() : null,
    createdAt: typeof raw.createdAt === 'string' && raw.createdAt.trim() ? raw.createdAt.trim() : null,
  }
}

const normalizePasskeys = (payload: Record<string, unknown>): PasskeyCredential[] => {
  const rootNode = payload.passkeys
  const candidates =
    rootNode && typeof rootNode === 'object' && Array.isArray((rootNode as { credentials?: unknown }).credentials)
      ? ((rootNode as { credentials: unknown[] }).credentials ?? [])
      : []
  return candidates.map(toCredential).filter((item): item is PasskeyCredential => item !== null)
}

const requestPasskeyApi = async ({
  authHeaders,
  path,
  init,
  fallbackError,
}: {
  authHeaders: LegacyAuthHeaders
  path: string
  init: RequestInit
  fallbackError: string
}): Promise<Record<string, unknown>> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload } = await requestAuthed({
    token,
    path,
    init: {
      ...init,
      headers: authHeaders(),
    },
  })
  if (!response.ok) {
    throw new Error(toPayloadMessage(payload, fallbackError))
  }
  return payload
}

export const requestPasskeys = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<PasskeyCredential[]> => {
  const payload = await requestPasskeyApi({
    authHeaders,
    path: PASSKEY_LIST_PATH,
    init: { method: 'GET' },
    fallbackError: t('profile.security.passkeys.error.load'),
  })
  return normalizePasskeys(payload)
}

export const requestPasskeyRegisterOptions = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<Record<string, unknown>> => {
  const payload = await requestPasskeyApi({
    authHeaders,
    path: PASSKEY_REGISTER_OPTIONS_PATH,
    init: { method: 'POST' },
    fallbackError: t('profile.security.passkeys.error.registerStart'),
  })
  const options = payload.options
  if (options && typeof options === 'object') {
    return options as Record<string, unknown>
  }
  return payload
}

export const requestPasskeyRegisterVerify = async ({
  authHeaders,
  response,
  nickname,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  response: Record<string, unknown>
  nickname?: string
  t: Translator
}): Promise<void> => {
  await requestPasskeyApi({
    authHeaders,
    path: PASSKEY_REGISTER_VERIFY_PATH,
    init: {
      method: 'POST',
      body: JSON.stringify({
        response,
        nickname: nickname?.trim() || undefined,
      }),
    },
    fallbackError: t('profile.security.passkeys.error.registerVerify'),
  })
}

export const requestRemovePasskey = async ({
  authHeaders,
  passkeyId,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  passkeyId: string
  t: Translator
}): Promise<void> => {
  const encodedId = encodeURIComponent(passkeyId)
  await requestPasskeyApi({
    authHeaders,
    path: `/passkeys/${encodedId}`,
    init: { method: 'DELETE' },
    fallbackError: t('profile.security.passkeys.error.remove'),
  })
}
